// ---------------------------------------------------------------------------
// createProjectFromTemplate — POST to platform.cogo.xyz Worker. Atomic
// preset+project create from a template selection. Returns the new IDs.
// Mirrors save-design-system-library.ts auth pattern.
// ---------------------------------------------------------------------------

import { getStudioAccessToken } from "@/services/auth-provider";
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaderRecord,
} from "@/services/cogo-platform-config";
import {
  withStudioProjectPlanSettings,
  type LegacyAppTarget,
  type StudioProjectPlan,
} from "./studio-project-plan";

export interface CreateFromTemplateParams {
  templateId: string;
  projectName?: string;
  description?: string;
  appTarget?: LegacyAppTarget;
  studioProjectPlan?: StudioProjectPlan;
}

export interface CreateFromTemplateResult {
  success: boolean;
  projectId?: string;
  presetId?: string;
  templateContractVersion?: typeof CANONICAL_TEMPLATE_CREATE_CONTRACT_VERSION;
  templateSourceVersion?: string;
  templateSourceHash?: string;
  templateSourceRef?: string;
  templateManifestHash?: string;
  templateDesignSystemHash?: string;
  templateCatalogHash?: string;
  templateBlocksHash?: string;
  templateBlockCount?: number;
  templateAuthorityScope?: "manifest-design-system-and-blocks";
  error?: string;
}

interface CreateFromTemplateApiResponse {
  template_contract_version?: unknown;
  project_id?: unknown;
  preset_id?: unknown;
  template_source_version?: unknown;
  template_source_hash?: unknown;
  template_source_ref?: unknown;
  template_manifest_hash?: unknown;
  template_design_system_hash?: unknown;
  template_catalog_hash?: unknown;
  template_blocks_hash?: unknown;
  template_block_count?: unknown;
  template_authority_scope?: unknown;
  error?: unknown;
}

const TEMPLATE_SOURCE_VERSION_PATTERN =
  /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const SHA256_PATTERN = /^sha256:[0-9a-f]{64}$/;
export const CANONICAL_TEMPLATE_CREATE_CONTRACT_VERSION =
  "cogo.canonical-template-create.v1" as const;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isValidTemplateSourceVersion(value: unknown): value is string {
  return (
    typeof value === "string" && TEMPLATE_SOURCE_VERSION_PATTERN.test(value)
  );
}

function isValidSha256(value: unknown): value is string {
  return typeof value === "string" && SHA256_PATTERN.test(value);
}

function isPositiveBlockCount(value: unknown): value is number {
  return (
    typeof value === "number" && Number.isSafeInteger(value) && value > 0
  );
}

function isTemplateAuthorityScope(
  value: unknown,
): value is "manifest-design-system-and-blocks" {
  return value === "manifest-design-system-and-blocks";
}

async function getUserAccessToken(): Promise<string | null> {
  return getStudioAccessToken();
}

export async function createProjectFromTemplate(
  params: CreateFromTemplateParams,
): Promise<CreateFromTemplateResult> {
  const jwt = await getUserAccessToken();
  if (!jwt) return { success: false, error: "Sign in required" };

  try {
    const res = await fetch(cogoPlatformApiUrl("/api/projects/from-template"), {
      method: "POST",
      headers: withCogoDevProxyHeaderRecord({
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        template_contract_version:
          CANONICAL_TEMPLATE_CREATE_CONTRACT_VERSION,
        template_id: params.templateId,
        project_name: params.projectName ?? null,
        description: params.description ?? "",
        app_target: params.appTarget ?? "web",
        settings: withStudioProjectPlanSettings({}, params.studioProjectPlan),
      }),
    });
    const json = (await res
      .json()
      .catch(() => ({}))) as CreateFromTemplateApiResponse;
    if (!res.ok) {
      if (
        json.template_contract_version !==
          CANONICAL_TEMPLATE_CREATE_CONTRACT_VERSION &&
        res.status === 400 &&
        isNonEmptyString(json.error) &&
        /template_name.*required/i.test(json.error)
      ) {
        return {
          success: false,
          error:
            "Canonical template creation requires the platform backend upgrade",
        };
      }
      return {
        success: false,
        error: isNonEmptyString(json.error) ? json.error : `HTTP ${res.status}`,
      };
    }
    const projectId = json.project_id;
    const presetId = json.preset_id;
    const templateContractVersion = json.template_contract_version;
    const templateSourceVersion = json.template_source_version;
    const templateSourceHash = json.template_source_hash;
    const templateSourceRef = json.template_source_ref;
    const templateManifestHash = json.template_manifest_hash;
    const templateDesignSystemHash = json.template_design_system_hash;
    const templateCatalogHash = json.template_catalog_hash;
    const templateBlocksHash = json.template_blocks_hash;
    const templateBlockCount = json.template_block_count;
    const templateAuthorityScope = json.template_authority_scope;
    if (
      templateContractVersion !== CANONICAL_TEMPLATE_CREATE_CONTRACT_VERSION
    ) {
      await rollbackIncompleteTemplateCreate(
        jwt,
        isNonEmptyString(projectId) ? projectId : undefined,
        isNonEmptyString(presetId) ? presetId : undefined,
      );
      return {
        success: false,
        error:
          "Canonical template creation requires the platform backend upgrade",
      };
    }
    const responseIdentityIsValid =
      isNonEmptyString(projectId) &&
      isNonEmptyString(presetId) &&
      isValidTemplateSourceVersion(templateSourceVersion) &&
      isValidSha256(templateSourceHash) &&
      isNonEmptyString(templateSourceRef) &&
      isValidSha256(templateManifestHash) &&
      isValidSha256(templateDesignSystemHash) &&
      isValidSha256(templateCatalogHash) &&
      isValidSha256(templateBlocksHash) &&
      isPositiveBlockCount(templateBlockCount) &&
      isTemplateAuthorityScope(templateAuthorityScope);
    if (!responseIdentityIsValid) {
      await rollbackIncompleteTemplateCreate(
        jwt,
        isNonEmptyString(projectId) ? projectId : undefined,
        isNonEmptyString(presetId) ? presetId : undefined,
      );
      return {
        success: false,
        error: "Malformed response (invalid canonical template source identity)",
      };
    }
    return {
      success: true,
      projectId,
      presetId,
      templateContractVersion,
      templateSourceVersion,
      templateSourceHash,
      templateSourceRef,
      templateManifestHash,
      templateDesignSystemHash,
      templateCatalogHash,
      templateBlocksHash,
      templateBlockCount,
      templateAuthorityScope,
    };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e.message : "Network error",
    };
  }
}

async function rollbackIncompleteTemplateCreate(
  jwt: string,
  projectId?: string,
  presetId?: string,
): Promise<void> {
  if (projectId) {
    await fetch(
      cogoPlatformApiUrl(`/api/projects/${encodeURIComponent(projectId)}`),
      {
        method: "DELETE",
        headers: withCogoDevProxyHeaderRecord({
          Authorization: `Bearer ${jwt}`,
        }),
      },
    ).catch(() => undefined);
  }
  if (presetId) {
    await fetch(
      cogoPlatformApiUrl(`/api/design-systems/${encodeURIComponent(presetId)}`),
      {
        method: "DELETE",
        headers: withCogoDevProxyHeaderRecord({
          Authorization: `Bearer ${jwt}`,
        }),
      },
    ).catch(() => undefined);
  }
}
